import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useBackupStatus } from './useAdminStatus';

interface BackupStatusWidgetProps {
    viewerToken?: string;
}

// Formats a byte count as a short human readable size
export const formatBytes = (bytes: number) => {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
    return `${Math.round(bytes / 1024)} KB`;
};

const BackupStatusWidget: React.FC<BackupStatusWidgetProps> = ({ viewerToken }) => {
    const { t } = useTranslation();
    const { backupStatus, loading } = useBackupStatus(viewerToken);

    // Map the backup status onto a dot color class
    const status = backupStatus.status;
    const dotClass = status === 'ok' ? 'admin-status-ok'
        : status === 'warning' || status === 'running' ? 'admin-status-warning'
        : 'admin-status-critical';

    // Get the moment and size of the newest backup record
    const latest = backupStatus.latest;
    const backupMoment = latest ? dayjs(latest.finished_at || latest.started_at) : null;
    const backupSize = latest && latest.size_bytes ? formatBytes(latest.size_bytes) : null;

    // Formats the backup moment as today, yesterday or a date
    const formatBackupMoment = () => {
        if (!backupMoment) return '';
        const time = backupMoment.format('HH:mm');
        if (backupMoment.isSame(dayjs(), 'day')) {
            return t('dashboard.monitoring.admin.backup.today_at', { time });
        }
        if (backupMoment.isSame(dayjs().subtract(1, 'day'), 'day')) {
            return t('dashboard.monitoring.admin.backup.yesterday_at', { time });
        }
        return backupMoment.format('DD-MM HH:mm');
    };

    // Build the main line and the subline per status
    let mainLine = t('dashboard.monitoring.admin.backup.status_missing');
    let subLine = latest
        ? [formatBackupMoment(), backupSize].filter(Boolean).join(' · ')
        : t('dashboard.monitoring.admin.backup.no_backups');
    if (status === 'ok' || status === 'warning') {
        mainLine = formatBackupMoment();
        subLine = backupSize || '';
    } else if (status === 'running') {
        mainLine = t('dashboard.monitoring.admin.backup.status_running');
        subLine = formatBackupMoment();
    } else if (status === 'failed') {
        mainLine = t('dashboard.monitoring.admin.backup.status_failed');
        subLine = formatBackupMoment();
    }

    return (
        <div className='dashboard-card admin-status-widget'>
            <div className='admin-status-header'>
                <h6 className='title'>{t('dashboard.monitoring.admin.backup.title')}</h6>
                {!loading && status && <span className={`admin-status-dot ${dotClass}`} />}
            </div>
            {loading ? (
                <div className='admin-status-loading'><div className='loader' /></div>
            ) : (
                <div className='admin-status-body'>
                    <div className='admin-status-value admin-status-value-medium'>{mainLine}</div>
                    <div className='admin-status-footer'>
                        <span className='admin-status-line-detail'>{subLine}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BackupStatusWidget;
